import { useEffect } from "react";

/**
 * Hook to handle document visibility changes and trigger a callback when the document becomes visible
 *
 * @param callback - Function to call when document becomes visible
 * @param condition - Optional condition that must be true for callback to execute (e.g., documentId exists)
 *
 * @example
 * // Simple usage
 * useDocumentVisibility(() => {
 *   documentState.incrementViewerKey();
 * });
 *
 * @example
 * // With condition
 * useDocumentVisibility(
 *   () => documentState.incrementViewerKey(),
 *   documentState.documentId !== null
 * );
 */
export const useDocumentVisibility = (
  callback: () => void,
  condition: boolean = true
): void => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && condition) {
        callback();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [callback, condition]);
};
